import { Logout, useGetIdentity, UserIdentity, useTheme, useThemesContext } from "react-admin";
import { useNavigate } from "react-router-dom";
import { SolarMenuUserItem, useSolarSidebarActiveMenu, usePrimarySidebarState } from "@react-admin/ra-navigation";
import { Avatar, Box, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Typography } from "@mui/material";
import { AccountCircle, DarkMode, LightMode } from "@mui/icons-material";
import { PartnerSwitcherMenuItem } from "../../partner/PartnerSwitcherMenuItem";

// ─── User Profile Menu Item (bottom of sidebar) ────────────────────────────

export const UserProfileMenuItem = () => {
  const { data: identity, isLoading } = useGetIdentity();
  const navigate = useNavigate();
  const [, setSecondarySidebarOpener] = useSolarSidebarActiveMenu();
  const [, setIsPrimarySidebarOpen] = usePrimarySidebarState();

  const handleNavigate = (path: string) => {
    void navigate(path);
    setSecondarySidebarOpener(null);
    setIsPrimarySidebarOpen(false);
  };

  return (
    <SolarMenuUserItem
      subMenu={
        <>
          {!isLoading && identity && <UserInfoHeader identity={identity} />}

          {/* Partner Switcher — only renders for users with partner-delegation:use permission */}
          <MenuList dense>
            <PartnerSwitcherMenuItem />
          </MenuList>
          <Divider />

          {/* Preferences */}
          <SectionLabel>Preferences</SectionLabel>
          <MenuList dense>
            <ThemeToggleItem />
          </MenuList>
          <Divider />

          {/* Account */}
          <SectionLabel>Account</SectionLabel>
          <MenuList dense>
            <MenuItem onClick={() => handleNavigate("/auth-info")} dense>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>Account Info</ListItemText>
            </MenuItem>
            <Logout />
          </MenuList>
        </>
      }
    />
  );
};

// ─── Internal Components ────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: string }) => (
  <Typography variant="subtitle2" sx={{ px: 2, pt: 1, pb: 0.5, opacity: 0.7 }}>
    {children}
  </Typography>
);

const UserInfoHeader = ({ identity }: { identity: UserIdentity }) => (
  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
        {(identity.fullName || identity.name || "U")[0].toUpperCase()}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {identity.fullName || identity.name || "User"}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }} noWrap>
          {identity.email}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ThemeToggleItem = () => {
  const { darkTheme } = useThemesContext();
  const [theme, setTheme] = useTheme();

  if (!darkTheme) return null;

  const isDark = theme === "dark";

  return (
    <MenuItem onClick={() => setTheme(isDark ? "light" : "dark")} dense>
      <ListItemIcon>{isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}</ListItemIcon>
      <ListItemText>{isDark ? "Light Mode" : "Dark Mode"}</ListItemText>
    </MenuItem>
  );
};
